/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiDescriptionList,
  EuiTextColor,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiFlexGrid,
  EuiPanel,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { SaasUserResponse } from '@modules/cloud-api/v1/types'
import type { UserProfileData } from '@modules/ui-types'
import { CuiRouterLink } from '@modules/cui/RouterButton'
import { organizationOverviewUrl } from '@modules/urls/organizations'
import { useGetUserMembershipsQuery } from '@modules/organization-lib/hooks'

import UserEmailDashboardLink from '@/components/User/UserEmailDashboardLink'
import PageSection from '@/components/PageSection'
import UserEmailForm from '@/components/User/UserEmailForm'
import MfaManagement from '@/components/User/MfaManagement'
import ClearPrivatelyIdentifiableInformationButton from '@/components/User/ClearPrivatelyIdentifiableInformationButton'
import RequestDeletionButton from '@/components/User/RequestDeletionButton'
import ClearTransactionalEmailSuppressionsButton from '@/components/User/ClearTransactionalEmailSuppressionsButton'
import ResendVerificationEmail from '@/components/User//ResendVerificationEmail'
import GenerateResetPasswordLinkButton from '@/components/User/GenerateResetPasswordLinkButton'
import OktaGroups from '@/components/User/OktaGroups'

import UserAuditLogLink from '../UserAuditLogLink'
import OktaStatus from '../OktaStatus'

import type { FunctionComponent } from 'react'

type Props = {
  profile: SaasUserResponse
}

const UserOverview: FunctionComponent<Props> = ({ profile }) => {
  const { user } = profile
  const { email, domain, data, user_id } = user
  const {
    data: memberships,
    isLoading: isLoadingGetUserMemberships,
    isError: isErrorGetUserMemberships,
  } = useGetUserMembershipsQuery({
    userId: user_id,
  })

  const userId = String(user_id)

  const membershipOrgIds =
    !isErrorGetUserMemberships && memberships?.memberships
      ? memberships.memberships.map((membership) => membership.organization_id)
      : []

  // `data` is just an opaque object in the API contracts :-(
  const herokuEmail = (data as UserProfileData | undefined)?.heroku?.owner_email

  return (
    <EuiFlexGroup
      className='user-overview'
      style={{ maxWidth: '1200px' }}
      direction='column'
      gutterSize='s'
    >
      <EuiFlexGroup direction='row' gutterSize='xl'>
        <EuiFlexGroup direction='column' style={{ maxWidth: '800px' }}>
          <EuiFlexItem>
            <EuiDescriptionList
              titleProps={{ css: { maxWidth: 250 } }}
              compressed={true}
              type='responsiveColumn'
              style={{ columnGap: '60px' }}
              listItems={[
                {
                  title: (
                    <FormattedMessage id='user-overview.user-id-label' defaultMessage='User ID' />
                  ),
                  description: userId,
                },
                {
                  title: (
                    <FormattedMessage
                      id='user-overview.organization-id-label'
                      defaultMessage='Organization ID'
                    />
                  ),
                  description:
                    !isLoadingGetUserMemberships && membershipOrgIds.length > 0 ? (
                      <EuiFlexGrid
                        columns={3}
                        gutterSize='s'
                        alignItems='start'
                        style={{ maxWidth: 'fit-content' }}
                      >
                        {membershipOrgIds.map((orgId) => (
                          <EuiFlexItem key={orgId}>
                            <CuiRouterLink
                              data-test-subj='organization-link'
                              to={organizationOverviewUrl(orgId)}
                            >
                              {orgId}
                            </CuiRouterLink>
                          </EuiFlexItem>
                        ))}
                      </EuiFlexGrid>
                    ) : (
                      <FormattedMessage id='user-overview.no-organization' defaultMessage='None' />
                    ),
                },
                {
                  title: <FormattedMessage id='user-overview.user-email' defaultMessage='Email' />,
                  description: <UserEmailForm userId={user_id} email={email} />,
                },

                ...(domain === `heroku`
                  ? [
                      {
                        title: (
                          <FormattedMessage
                            id='user-overview.heroku-email'
                            defaultMessage='Email (Heroku)'
                          />
                        ),
                        description: (
                          <div>
                            {herokuEmail || (
                              <EuiTextColor color='subdued'>
                                <FormattedMessage
                                  id='user-overview.empty-heroku-email'
                                  defaultMessage='Not set'
                                />
                              </EuiTextColor>
                            )}
                          </div>
                        ),
                      },
                    ]
                  : []),
                {
                  title: (
                    <FormattedMessage
                      id='user-overview.mfa'
                      defaultMessage='Multifactor authentication'
                    />
                  ),
                  description: <MfaManagement userId={userId} email={email} />,
                },
                {
                  title: (
                    <FormattedMessage
                      id='user-overview.user-okta-group'
                      defaultMessage='Okta groups'
                    />
                  ),
                  description: <OktaGroups userId={user_id.toString()} />,
                },
                {
                  title: (
                    <FormattedMessage
                      id='user-overview.user-okta-status'
                      defaultMessage='Okta status'
                    />
                  ),
                  description: <OktaStatus userId={user_id.toString()} />,
                },
              ]}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiPanel hasShadow={false} hasBorder={true}>
          <EuiFlexGroup direction='column' gutterSize='xs'>
            <EuiFlexItem>
              <EuiTitle size='s'>
                <h4>
                  <FormattedMessage id='user-overview.actions-header' defaultMessage='Actions' />
                </h4>
              </EuiTitle>
            </EuiFlexItem>
            <EuiSpacer size='s' />
            <EuiFlexItem>
              <ClearPrivatelyIdentifiableInformationButton userId={userId} />
            </EuiFlexItem>

            <EuiFlexItem>
              <RequestDeletionButton userId={userId} email={email} />
            </EuiFlexItem>

            <EuiFlexItem>
              <GenerateResetPasswordLinkButton userId={userId} />
            </EuiFlexItem>

            <EuiFlexItem>
              <ClearTransactionalEmailSuppressionsButton userId={userId} />
            </EuiFlexItem>

            <EuiFlexItem>
              <ResendVerificationEmail email={email} />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiPanel>
      </EuiFlexGroup>
      <EuiFlexItem style={{ maxWidth: '500px' }}>
        <EuiSpacer />

        <PageSection
          iconType='logoKibana'
          title={
            <FormattedMessage
              id='user-overview.kibana-section'
              defaultMessage='Kibana dashboards'
            />
          }
        >
          <UserEmailDashboardLink email={email} />

          <EuiSpacer size='l' />

          <UserAuditLogLink userId={userId} />
        </PageSection>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default UserOverview
